import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { IPropertyAnalyticsItem } from "../types/ownerDashboard.types";
import { formatEngagementTime } from "../utils/formatEngagementTime";
import { resolvePropertyAnalyticsLabel } from "../utils/buildPropertyAnalyticsFilterOptions";
import DashboardPagination from "../components/DashboardPagination";
import { PROPERTY_ANALYTICS_TABLE_PAGE_SIZE } from "./constants";

interface PropertyAnalyticsTableProps {
  items: IPropertyAnalyticsItem[];
}

function resolvePropertyPath(item: IPropertyAnalyticsItem): string {
  const pagePath = item.page_path?.trim();
  if (pagePath?.startsWith("/")) {
    return pagePath;
  }

  return `/imovel/${item.property_id}`;
}

export default function PropertyAnalyticsTable({ items }: PropertyAnalyticsTableProps) {
  const history = useHistory();
  const [page, setPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(items.length / PROPERTY_ANALYTICS_TABLE_PAGE_SIZE));

  const paginatedItems = useMemo(() => {
    const startIndex = (page - 1) * PROPERTY_ANALYTICS_TABLE_PAGE_SIZE;
    return items.slice(startIndex, startIndex + PROPERTY_ANALYTICS_TABLE_PAGE_SIZE);
  }, [items, page]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  if (items.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-[#8b949e]">
        Nenhum imóvel com visualizações no período selecionado.
      </p>
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <div className="overflow-x-auto rounded-2xl border border-white/[0.06]">
        <table className="min-w-full text-left text-xs sm:text-sm">
          <thead className="bg-white/[0.03] text-[10px] uppercase tracking-wide text-[#6e7681] sm:text-[11px]">
            <tr>
              <th className="px-3 py-2.5 font-semibold">Imóvel</th>
              <th className="px-3 py-2.5 font-semibold">Visualizações</th>
              <th className="px-3 py-2.5 font-semibold">Usuários</th>
              <th className="hidden px-3 py-2.5 font-semibold md:table-cell">Views/usuário</th>
              <th className="hidden px-3 py-2.5 font-semibold lg:table-cell">Engajamento</th>
              <th className="hidden px-3 py-2.5 font-semibold xl:table-cell">Cliques</th>
              <th className="hidden px-3 py-2.5 font-semibold xl:table-cell">Agendamentos</th>
            </tr>
          </thead>
          <tbody>
            {paginatedItems.map((item) => {
              const propertyPath = resolvePropertyPath(item);

              return (
                <tr
                  key={item.property_id}
                  role="link"
                  tabIndex={0}
                  onClick={() => history.push(propertyPath)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      history.push(propertyPath);
                    }
                  }}
                  className="cursor-pointer border-t border-white/[0.06] text-[#c9d1d9] transition-colors hover:bg-white/[0.04] focus-visible:bg-white/[0.04] focus-visible:outline-none"
                  aria-label={`Abrir página do imóvel ${resolvePropertyAnalyticsLabel(item)}`}
                >
                  <td className="max-w-[14rem] px-3 py-2.5 font-medium text-[#6ea8ff] sm:max-w-none">
                    <span className="line-clamp-2">{resolvePropertyAnalyticsLabel(item)}</span>
                    <span className="mt-0.5 block text-[0.65rem] font-normal text-[#6e7681]">
                      ID {item.property_id}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">{item.screen_page_views}</td>
                  <td className="px-3 py-2.5">{item.active_users}</td>
                  <td className="hidden px-3 py-2.5 md:table-cell">
                    {item.screen_page_views_per_active_user.toFixed(2)}
                  </td>
                  <td className="hidden px-3 py-2.5 lg:table-cell">
                    {formatEngagementTime(item.average_engagement_time_seconds)}
                  </td>
                  <td className="hidden px-3 py-2.5 xl:table-cell">{item.schedule_click_count}</td>
                  <td className="hidden px-3 py-2.5 xl:table-cell">
                    {item.schedule_key_event_count}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <DashboardPagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
    </div>
  );
}
